// utils/validationUtils.ts
import { formatCpfOrCnpj as formatCpfOrCnpjUnificado } from './documento';

// Delega para o utilitário unificado (src/utils/documento.ts), que já aceita CNPJ alfanumérico.
export const formatCpfOrCnpj = formatCpfOrCnpjUnificado;
 
export const formatCep = (cep: string): string => {
    // Remove qualquer caractere que não seja dígito
    const cleanCep = cep.replace(/\D/g, '');
 
    // Verifica se o CEP tem o tamanho correto (8 dígitos)
    if (cleanCep.length !== 8) {
        return 'CEP inválido';
    }
 
    // Formata o CEP no formato 00000-000
    return cleanCep.replace(/(\d{5})(\d{3})/, '$1-$2');
}
 
export const formatPhoneNumber = (phoneNumber: string): string => {
    const cleanedPhoneNumber = phoneNumber.replace(/\D/g, ''); // Remove todos os caracteres não numéricos
 
    // Verifica se o número tem o formato válido de 10 ou 11 dígitos
    if (cleanedPhoneNumber.length === 10) {
        return cleanedPhoneNumber.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3'); // Formato (XX) XXXX-XXXX
    } else if (cleanedPhoneNumber.length === 11) {
        return cleanedPhoneNumber.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3'); // Formato (XX) XXXXX-XXXX
    }
 
    return phoneNumber;
};
 
export const formatDefaultValuesToString = (value: any): string => {
    if (value === null || value === undefined) return "-";
 
    if (typeof value === "boolean") return value ? "Sim" : "Não";
 
    if (Array.isArray(value)) return renderArray(value);
 
    if (typeof value === "object" && !Array.isArray(value))
        return Object.values(value).join(" - ");
 
    if (isDateValue(value))
        return formatDate(value);
 
    return value.toString();
};
 
export const formatSpecificValuesToString = (key: any, value: any): string => {
    if (value === null || value === undefined) return "-";
 
    if (typeof value === "boolean") return value ? "Sim" : "Não";
 
    if (Array.isArray(value)) return renderArray(value);
 
    if (typeof value === "object" && !Array.isArray(value))
        return formatObjectValues(key, value);
 
    if (isDateValue(value)) {
        if (key === "idade") return calculateAgeToString(value);
        return formatDate(value);
    }
 
    return formatNonObjectValues(key, value);
};
 
const formatNonObjectValues = (key: any, value: any): string => {
    switch (key) {
        case "valor":
        case "valorPendencia":
        case "valorTotal":
        case "valorUltimaOcorrencia":
            return formatValueReais(value);
 
        case "CEP":
            return formatCep(value.toString());
 
        case "DOCUMENTO":
        case "CPFCNPJ":
            return formatCpfOrCnpj(value);
 
        default:
            return value.toString();
    }
};
 
const formatObjectValues = (key: any, value: any): string => {
    switch (key) {
        case "protocolo":
            return [
                `Número: ${value.numero || "N/A"}`,
                `Digito: ${value.digito || "N/A"}`,
            ].join(" - ");
 
        case "operador":
            return [
                `Código: ${value.codigo || "N/A"}`,
                `Nome: ${value.nome || "N/A"}`,
            ].join(" - ");
 
        case "endereco":
            const addressParts = [
                value.logradouro,
                value.numero,
                value.bairro,
                value.complemento,
                `${value.cidade?.estado?.siglaUf || ""} ${value.cidade?.nome || ""}`,
                formatCep(value.cep.toString()),
            ];
            return addressParts
                .filter((part) => part?.toString().trim())
                .join(" - ");
 
        case "telefoneCelular":
        case "telefoneResidencial":
        case "telefoneAssociado":
            return formatPhoneNumber(`${value.numeroDdd || ""}${value.numero || ""}`.trim());
 
        case "situacaoCpf":
            return [
                `Descrição Situação: ${value.descricaoSituacao || "N/A"}`,
                `UF: ${value.uf || "N/A"}`,
                `Data: ${value.dataSituacao ? formatDate(value.dataSituacao) : "N/A"}`,
            ].join(" - ");
 
        case "cpf":
            return [
                `CPF: ${value.numero ? formatCpfOrCnpj(value.numero) : "N/A"}`,
                `Região Origem: ${value.regiaoOrigem || "N/A"}`,
            ].join(" - ");
 
        case "origemAssociado":
        case "cidadeAssociado":
        case "cidade":
            return [
                `${value.estado?.siglaUf || "N/A"}`,
                `${value.nome || "N/A"}`,
            ].join(" - ");
 
        case "moeda":
            return value.simbolo || "-";
 
        default:
            return JSON.stringify(value);
    }
};
 
export const isDateValue = (value: any): boolean => {
    if (value instanceof Date)
        return !isNaN(value.getTime());
 
    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return !isNaN(parsedDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(value);
    }
 
    return false;
};
 
export const formatValueReais = (valor: number | string | null | undefined): string => {
    if (valor === null || valor === undefined || (typeof valor === "string" && !valor.trim())) 
        return "R$ 0,00";
 
    let numericValue: number;
 
    if (typeof valor === "string") {
        const valorSemVirgula = valor.replace(",", ".");
        numericValue = parseFloat(valorSemVirgula);
 
        if (isNaN(numericValue)) 
            return "R$ 0,00";
    } else 
        numericValue = valor;
 
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(numericValue);
};
 
export const formatDate = (data: any): string => {
    const date = new Date(data);
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
    };
 
    return date.toLocaleDateString("pt-BR", options);
};
 
export const calculateAgeToString = (dateOfBirth: any): string => {
    const birth = new Date(dateOfBirth);
    const today = new Date();
 
    let age = today.getFullYear() - birth.getFullYear();
    const month = today.getMonth() - birth.getMonth();
 
    // Se o mês atual for anterior ao mês de nascimento, ou for o mês de nascimento, mas o dia atual ainda não passou, diminui um ano
    if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
 
    return age.toString();
};
 
export const renderArray = (array: any[]): string => {
    return array
        .filter((item) => item !== null && item.toString().trim() !== "")
        .map((item) => (typeof item === "object" ? JSON.stringify(item) : item.toString()))
        .join(", ");
};
 
export const isValidRenavam = (renavam: string) => {
 
    let d: any = renavam.split("");
    let soma: number = 0,
        valor = 0,
        digito = 0,
        x = 0;
 
    for (var i = 5; i >= 2; i--) {
        soma += d[x] * i;
        x++;
    }
 
    valor = soma % 11;
 
    if (valor == 11 || valor == 0 || valor >= 10) {
        digito = 0;
    } else {
        digito = valor;
    }
 
    if (digito == d[4]) {
        return true;
    } else {
        return false;
    }
}
 
// Validação de CPF - delega para o utilitário unificado (src/utils/cpf.ts).
export { isValidCpf as isValidCPF } from './cpf';

// Validação de CNPJ - delega para o utilitário unificado (src/utils/cnpj.ts), que já aceita CNPJ alfanumérico.
export { isValidCnpj as isValidCNPJ } from './cnpj';
 
// Validação de e-mail
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
};

// Validação de um ou mais e-mails separados por ponto e vírgula (;)
// Qualquer outro separador (vírgula, espaço, etc.) invalida o campo.
export const isValidMultiEmail = (value: string): boolean => {
    if (isEmpty(value)) return false;

    const emails = value.split(';').map((e) => e.trim());
    if (emails.some((e) => e === '')) return false;

    return emails.every((e) => EMAIL_REGEX.test(e));
};

// Validação de campo vazio
export const isEmpty = (value: string | null | undefined): boolean => {
    return value === null || value === undefined || value.trim() === '';
};
 
export const openBase64Data = (base64Data: string, dataType: string) => {
    if (!base64Data) return; // Evita erro se o campo estiver vazio
  
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    const byteArray = new Uint8Array(byteArrays);
    const blob = new Blob([byteArray], { type: dataType });
  
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Remove a máscara de um documento (CPF/CNPJ) mantendo apenas letras e dígitos, em caixa alta.
 * CPF é sempre numérico; o novo CNPJ alfanumérico admite letras nas 12 primeiras posições.
 */
export const limparDocumento = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Máscara de CPF: 000.000.000-00
export const maskCpf = (value: string): string => {
    let v = limparDocumento(value).replace(/[^0-9]/g, '').slice(0, 11);
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    return v;
};

// Máscara do novo CNPJ alfanumérico: AA.AAA.AAA/AAAA-00
// As 12 primeiras posições aceitam letras/dígitos; os 2 dígitos verificadores são numéricos.
export const maskCnpj = (value: string): string => {
    const limpo = limparDocumento(value);
    const base = limpo.slice(0, 12);                        // 12 posições alfanuméricas
    const dv = limpo.slice(12, 14).replace(/[^0-9]/g, '');  // DV apenas numérico
    let v = base + dv;
    v = v.replace(/^([A-Z0-9]{2})([A-Z0-9])/, '$1.$2');
    v = v.replace(/^([A-Z0-9]{2})\.([A-Z0-9]{3})([A-Z0-9])/, '$1.$2.$3');
    v = v.replace(/\.([A-Z0-9]{3})([A-Z0-9])/, '.$1/$2');
    v = v.replace(/([A-Z0-9]{4})([A-Z0-9])/, '$1-$2');
    return v;
};

// Aplica a máscara adequada conforme o documento seja CPF ou CNPJ.
// CPF é sempre numérico; havendo letras ou mais de 11 posições, trata-se de CNPJ.
export const maskCpfCnpj = (value: string): string => {
    const limpo = limparDocumento(value);
    if (/[A-Z]/.test(limpo) || limpo.length > 11) {
        return maskCnpj(value);
    }
    return maskCpf(value);
};